import React, { useCallback, useMemo, useContext, useState } from "react";
import Timeago from "react-timeago";
import cn from "clsx";
import * as ReactTimeago from "react-timeago";
import Barcode from "react-barcode";
import { AppStateContext } from "../../containers/App";
import { NOTIFICATION_TYPE, NOTIFICATION_ACTION } from "../../types";
import { get, hasAndIsNotEmpty } from "../../utils/helpers";
import { timeAgoFormatters } from "../../utils/i18n";
import useT from "../../hooks/useT";
import Button from "../Button";
import "./Notification.css";

export interface NotificationObject {
  /**
   * The aggregate ID for the notification which groups multiple
   * notifications to a similar subject, usually the notification's
   * request ID.
   */
  aggregateId?: string | number;
  /**
   * The notification's type:
   *
   * - `success`
   * - `error`
   * - `warning`
   * - `info`
   */
  type?: string; // NotificationType
  /**
   * The notification's action. This will pre-populate the title, body and
   * extra content areas if it corresponds to a specific action:
   *
   * - `customerData.created`
   * - `customerData.updated`
   * - `customerData.failure`
   * - `customerData.apiError`
   * - `customerData.verify`
   */
  action?: string; // NotificationAction
  /**
   * The title to display. Used only if not specifying a notification
   * action.
   */
  title?: React.ReactNode | React.ReactNodeArray;
  /**
   * The body message to display. Used only if not specifying a notification
   * action.
   */
  body?: React.ReactNode | React.ReactNodeArray;
  /**
   * Extra content to display. Used only if not specifying a notification action.
   */
  extraContent?: React.ReactNode | React.ReactNodeArray;
  /** Timestamp of when the notification was created. */
  timestamp?: number;
  /** Timestamp to manipuate TimeAgo */
  timeAgoTimestamp?: number;
  /** If the notification has been read or not. */
  read?: boolean;
  /** I18n interpolation params. */
  params?: { [propName: string]: any };
}

export interface NotificationProps extends NotificationObject {
  /** Display any extra content. */
  children?: React.ReactNode | React.ReactNodeArray;
  /** Event handler when the confirm button is clicked. */
  onConfirm?: React.MouseEventHandler<HTMLButtonElement>;
  /** Event handler when the cancel button is clicked. */
  onCancel?: React.MouseEventHandler<HTMLButtonElement>;
  /** Event handler when the close button is clicked. */
  onClose?: React.MouseEventHandler<HTMLButtonElement>;
  /** Event handler when the "Display customer" button is clicked. */
  onDisplay?: (customerId: string) => void;
  /** Formatters to display the notification timestamp. */
  formatter?: ReactTimeago.Formatter;
}

/*
 * Adjusts the timestamp so the calculation of TimeAgo
 * will fit the calaulation of the filter
 * (if the user selected filter of 2 days ago,
 *   we want all the notification within that date,
 *   and we want it to display "2 days ago")
 */
function adjustTimeAgo(timestamp: any): number {
  const now: number = new Date().getTime();
  if (now - timestamp < 86400000) {
    return timestamp;
  }
  return new Date(timestamp).setHours(new Date().getHours());
}

export default function Notification({
  aggregateId = "",
  type = "",
  action = "",
  title = "",
  body = "",
  extraContent = "",
  timestamp = 0,
  read = true,
  params = {},
  children,
  onConfirm,
  onCancel,
  onClose,
  onDisplay,
  formatter = timeAgoFormatters.en
}: NotificationProps) {
  const { t, superT } = useT();
  const [appState] = useContext(AppStateContext);
  const [displayFullBody, setDisplayFullBody] = useState(false);
  const config = appState.config;

  const n: NotificationObject = {
    aggregateId,
    type,
    action,
    timestamp,
    title,
    body,
    extraContent,
    read,
    params: {
      br: <br />,
      ...params
    }
  };

  // Display the customer information only if a customerId is present in the notification's params

  const showButtonDisplay = useMemo(
    () =>
      config.customise.notifications.enableDisplayCustomer &&
      onDisplay &&
      params.hasOwnProperty("customerId") &&
      params.customerId,
    [params]
  );
  const handleClickDisplay = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      if (showButtonDisplay && onDisplay) {
        onDisplay(params.customerId);
      }
    },
    [showButtonDisplay]
  );

  // translate honorific prefix
  if (n.params && n.params.honorificPrefixLabelKey) {
    let title = t(n.params.honorificPrefixLabelKey);
    if (title === n.params.honorificPrefixLabelKey) {
      title = n.params.honorificPrefix;
    }
    n.params.honorificPrefix = title;
    n.params.title = title; // some use title in translation files
  }
  let isErrorMessageType = false;
  switch (action) {
    case NOTIFICATION_ACTION.CUSTOMER_DATA_CREATED:
      n.type = NOTIFICATION_TYPE.SUCCESS;
      n.title = superT("notifications.messages.success.title");
      n.body = superT(
        "notifications.messages.customerSaved.created.body",
        n.params
      );
      if (n.params) {
        n.params.barcode_content = n.params.customerId;
      }
      break;

    case NOTIFICATION_ACTION.CUSTOMER_DATA_UPDATED:
      n.type = NOTIFICATION_TYPE.SUCCESS;
      n.title = superT("notifications.messages.success.title");
      n.body = superT(
        "notifications.messages.customerSaved.updated.body",
        n.params
      );
      break;

    case NOTIFICATION_ACTION.CUSTOMER_DATA_PENDING:
      n.type = NOTIFICATION_TYPE.SUCCESS;
      n.title = superT("notifications.messages.success.title");
      n.body = superT(
        "notifications.messages.customerSaved.pending.body",
        n.params
      );
      break;

    case NOTIFICATION_ACTION.CUSTOMER_DATA_FAILURE:
      isErrorMessageType = true;
      n.type = NOTIFICATION_TYPE.ERROR;
      n.title = superT("notifications.messages.errors.title");
      n.body = superT(
        "notifications.messages.customerSaved.errors.dataSource.body",
        n.params
      );
      n.extraContent = hasAndIsNotEmpty(params, "traceToken") ? (
        <div className="trace-token">{get(params, "traceToken")}</div>
      ) : null;
      break;

    case NOTIFICATION_ACTION.CUSTOMER_DATA_API_ERROR:
      isErrorMessageType = true;
      n.type = NOTIFICATION_TYPE.ERROR;
      n.title = superT("notifications.messages.errors.title");
      n.body = superT(
        "notifications.messages.customerSaved.errors.server.body",
        n.params
      );
      n.extraContent = hasAndIsNotEmpty(params, "traceToken") ? (
        <div className="trace-token">{get(params, "traceToken")}</div>
      ) : null;
      break;

    case NOTIFICATION_ACTION.CUSTOMER_NOT_FOUND:
      isErrorMessageType = true;
      n.type = NOTIFICATION_TYPE.ERROR;
      n.title = superT("notifications.messages.customerRetrieval.title");
      n.body = superT(
        "notifications.messages.customerRetrieval.body",
        n.params
      );
      n.extraContent = hasAndIsNotEmpty(params, "traceToken") ? (
        <div className="trace-token">{get(params, "traceToken")}</div>
      ) : null;
      break;

    case NOTIFICATION_ACTION.CUSTOMER_DATA_VERIFY:
      n.type = NOTIFICATION_TYPE.WARNING;
      n.title = superT("notifications.messages.customerVerifyData.title");
      n.body = superT(
        "notifications.messages.customerVerifyData.body",
        n.params
      );
      n.extraContent = (
        <div className="button-group">
          {!!onConfirm && (
            <Button
              label={t(
                "notifications.messages.customerVerifyData.buttons.confirm.label"
              )}
              shape="solid"
              theme="primary"
              onClick={onConfirm}
            />
          )}
          {!!onCancel && (
            <Button
              label={t(
                "notifications.messages.customerVerifyData.buttons.cancel.label"
              )}
              shape="solid"
              theme="secondary"
              onClick={onCancel}
            />
          )}
        </div>
      );
      break;
    default:
  }

  const enablePartialBody =
    config.customise.notifications.displayPartialBody && isErrorMessageType;

  n.timeAgoTimestamp = adjustTimeAgo(n.timestamp);

  return (
    <div
      className={cn(
        "notification",
        `notification-type-${n.type}`,
        !read && "is-unread",
        !!onClose && "has-close"
      )}
    >
      {!!n.title && <h3 className="notification-title">{n.title}</h3>}

      {!!n.body && (
        <div className="notification-body">
          {enablePartialBody && !displayFullBody
            ? (Array.isArray(n.body) && n.body[0]) ||
              n.body.toString().substring(0, 80) + "..."
            : n.body}
          {n.params && n.params.barcode_content ? (
            <div className="notification-barcode">
              <Barcode value={n.params.barcode_content} />
            </div>
          ) : null}
        </div>
      )}

      {!!n.extraContent && (!enablePartialBody || displayFullBody) && (
        <div className="notification-extra-content">{n.extraContent}</div>
      )}

      {!!n.body && enablePartialBody && (
        <div className="expand-buttons">
          {displayFullBody ? (
            <Button
              shape="link"
              label={t("notifications.messages.errors.buttonShowLess")}
              onClick={() => setDisplayFullBody(false)}
            />
          ) : (
            <Button
              shape="link"
              label={t("notifications.messages.errors.buttonShowMore")}
              onClick={() => setDisplayFullBody(true)}
            />
          )}
        </div>
      )}

      {children}
      {!!onClose && (
        <Button
          className="button-role-close"
          icon="close"
          ariaLabel={t("general.buttons.close.label")}
          onClick={onClose}
        />
      )}
      {showButtonDisplay && (
        <Button
          className="button-role-display"
          label={t("notifications.customerData.display.buttons.label")}
          shape="link"
          onClick={handleClickDisplay}
        />
      )}
      {n.timestamp && (
        <div className="notification-time">
          <Timeago
            date={n.timeAgoTimestamp}
            formatter={formatter || timeAgoFormatters.en}
            live={false}
          />
        </div>
      )}
    </div>
  );
}
